import { THEME } from '@common/constants/theme.constant';
import { FieldV2 } from '@common/general-ui/field-v2/field-v2.component';
import { IInputV2Props, inputStyles } from '@common/general-ui/input-v2/input-v2.component';
import { Readonly } from '@common/general-ui/readonly.component';
import { Global, SerializedStyles } from '@emotion/react';
import { Input as AntdInput } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from 'twin.macro';

export const InputAddonV2 = (props: IInputV2Props): JSX.Element => {
    const { size, errorMessage, helperText, value, validate: registerOptions, formatValue, ...rest } = props;

    const [val, setVal] = useState(props.defaultValue || props.value);

    useEffect(() => {
        if (props.value !== undefined) {
            setVal(formatValue ? formatValue(props.value as string) : props.value);
        }
    }, [props.value, formatValue]);

    useEffect(() => {
        if (props.defaultValue !== undefined) {
            setVal(formatValue ? formatValue(props.defaultValue as string) : props.defaultValue);
        }
    }, [props.defaultValue, formatValue]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;

            setVal(formatValue ? formatValue(newValue) : newValue);

            props.onChange &&
                props.onChange({
                    ...e,
                    target: {
                        ...e.target,
                        value: formatValue ? (formatValue(newValue) as string) : newValue,
                    },
                });
        },
        [props, formatValue],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            props.onBlur && props.onBlur({ ...e, target: { ...e.target, value: e.target.value } });
        },
        [props],
    );

    const formatText = useMemo(() => {
        return val !== undefined ? `${val}` : '';
    }, [val]);

    return (
        <FieldV2 {...props}>
            <Global styles={styles.dropdown} />
            <div tw="relative">
                {props.readOnly && <Readonly />}
                <AntdInput
                    {...rest}
                    prefix={props.prefix}
                    maxLength={props.maxLength}
                    tabIndex={props.readOnly ? -1 : 0}
                    type="text"
                    css={styles.input(!!props.readOnly, !!errorMessage)}
                    value={formatText}
                    onChange={handleChange}
                    onPaste={handleChange as unknown as React.ClipboardEventHandler}
                    onBlur={handleBlur}
                />
            </div>
        </FieldV2>
    );
};

export const styles = {
    input: (readOnly: boolean, hasError: boolean): (SerializedStyles | boolean)[] => [
        css`
            && .ant-input {
                ${inputStyles.input(readOnly, hasError)[0]}
                border-left: 0;
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
            }
            && .ant-input-group-addon {
                border-right: 1px solid ${THEME.GRAY_300};
                // color: ${THEME.TEXT_BLACK};
                margin: 0;
                padding: 0;
                border-radius: 4px;
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }
            && .ant-input-group-addon .ant-select {
                margin: 0;
            }
            && .ant-select-focused {
                // color: ${THEME.TEXT_BLACK};
                outline-color: transparent;
                outline-style: none;
                box-shadow: none;
            }
            && .ant-select-selector {
                color: ${THEME.TEXT_BLACK};
                border-color: transparent !important;
                box-shadow: none !important;
                max-height: 46px;
            }
        `,
    ],
    dropdown: css`
        && .ant-select-dropdown {
            width: 50% !important;
            max-width: 400px;
        }
        && .ant-select-item {
            font-weight: 300;
        }
        && .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
            font-weight: 400;
            background-color: transparent;
        }
    `,
};
